import {
  NavLink,
  Link as RouterLink,
  useLocation,
  useNavigate,
  useRouteLoaderData,
} from "react-router"
import {
  Rocket,
  BookOpen,
  Building2,
  MapPinIcon,
  VectorSquareIcon,
  SplitIcon,
  Database,
  Download,
  FlaskConical,
  ArrowUpRight,
  ArrowRight,
  Menu,
  type LucideIcon,
} from "lucide-react"
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
  navigationMenuTriggerStyle,
} from "~/components/ui/navigation-menu"
import { useEffect, useRef, useState } from "react"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "~/components/ui/sheet"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion"
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip"
import { Button, buttonVariants } from "~/components/ui/button"
import { captureEvent } from "~/client/posthog.client"
import { useDocsHref } from "~/docs-data/docs-link"
import { guides, isLive } from "~/docs-data/guides"
import type { CountryCatalog } from "~/docs-data/country-catalogs"
import type { DatasetEntry } from "~/docs-data/datasets"
import {
  buildCountrySummary,
  canonicalMetricSlugForType,
  comingSoonLabel,
  liveMethodologyEntries,
  methodologyForCountry,
  metricCanonicalName,
  sortCountriesForDocs,
} from "~/docs-data/helpers"
import type { DocsLocationType } from "~/server/models/docsCatalog.server"
import { cn } from "~/lib/utils"

type DocsCatalogData = {
  countries: CountryCatalog[]
  datasets: DatasetEntry[]
  locationTypes?: DocsLocationType[]
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MegaColumn = {
  /** Optional — when omitted, only the title is shown (used by Coverage so the
   * country flag emoji in the title acts as the visual marker). */
  icon?: LucideIcon
  title: string
  /**
   * When set, the entire heading block (icon + title + meta) becomes a single
   * clickable link with the same hover style as the items below — useful for
   * mega columns that have a canonical landing page (Coverage country
   * overview, Concepts location-type page, API anchored section, etc.).
   */
  to?: string
  meta: string
  /** Optional column-width hint as a flex-basis fraction (defaults to equal). */
  width?: string
  items: { title: string; to: string; trailing?: string }[]
  /** Optional inline link rendered at the bottom of the column, primary-colored. */
  tail?: { title: string; to: string }
}

type MegaPanel = {
  columns: MegaColumn[]
  /** Force a specific column count (wraps overflow). Defaults to columns.length. */
  gridCols?: number
  /** Optional secondary row beneath the columns (e.g. "Coming soon" pills). */
  extra?: React.ReactNode
  /** Quick links rendered in a bordered footer row. */
  footer?: { icon: LucideIcon; label: string; to: string }[]
}

type Section = {
  label: string
  to: string
  mega?: (data: DocsCatalogData) => MegaPanel
}

// ---------------------------------------------------------------------------
// Mega panel builders
// ---------------------------------------------------------------------------

function buildGuidesMega(): MegaPanel {
  const slugTitle = (slug: string): { title: string; to: string } => {
    const g = guides.find((x) => x.slug === slug)
    return { title: g?.title ?? slug, to: `/docs/guides/${slug}` }
  }
  const getStarted = ["first-query", "build-with-ai", "bulk-export", "no-code"]
  // Status-aware like the stories column: drafts drop out automatically.
  const useCases = [
    "site-selection",
    "brand-comparison",
    "store-profiling",
    "area-identification",
    "audience-profiling",
    "area-vitality",
    "custom-odm",
  ].filter((slug) => {
    const g = guides.find((x) => x.slug === slug)
    return g ? isLive(g) : false
  })
  // Status-aware: the column disappears while its guides are drafts
  // (customer stories are temporarily hidden this way).
  const stories = [
    "case-study-fintraffic-road-network",
    "case-study-event-visitor-insights",
    "case-study-scb-tourism-seasonal-shifts",
  ].filter((slug) => {
    const g = guides.find((x) => x.slug === slug)
    return g ? isLive(g) : false
  })
  return {
    columns: [
      {
        icon: BookOpen,
        title: "How-to guides",
        to: "/docs/guides",
        meta: "By goal",
        items: useCases.map(slugTitle),
      },
      {
        icon: Rocket,
        title: "Using the platform",
        to: "/docs/get-started",
        meta: "First steps & capabilities",
        items: getStarted.map(slugTitle),
      },

      ...(stories.length > 0
        ? [
            {
              icon: Building2,
              title: "Customer stories",
              to: "/docs/guides#customer-stories",
              meta: "How others use Pine59",
              items: stories.map(slugTitle),
            },
          ]
        : []),
    ],
  }
}

function buildCoverageMega(data: DocsCatalogData): MegaPanel {
  // Per-country location-type drilldown order. Only types that actually have
  // datasets in the country show up — countries with no `way` data don't get
  // a stranded Way row.
  const LOC_TYPES: { id: "place" | "area" | "way"; label: string }[] = [
    { id: "place", label: "Place" },
    { id: "area", label: "Area" },
    { id: "way", label: "Way" },
  ]
  return {
    gridCols: 3,
    // One column per country. The header is a single hover-able link to the
    // country catalog overview; underneath, each present location type links
    // to its location-type drilldown page with a dataset count.
    columns: sortCountriesForDocs(data.countries).map((c) => {
      const countryDs = data.datasets.filter((d) => d.country === c.countryCode)
      const items = LOC_TYPES.map(({ id, label }) => {
        const n = countryDs.filter((d) => d.locationType === id).length
        return n > 0
          ? {
              title: label,
              to: `/docs/datasets/${c.code}/location-types/${id}`,
              trailing: String(n),
            }
          : null
      }).filter((x): x is { title: string; to: string; trailing: string } => x !== null)
      // Methodology is country-scoped (it follows the country's signal), so
      // it hangs off the country column rather than sitting under Concepts.
      const methodologyItems =
        methodologyForCountry(c.countryCode).length > 0
          ? [{ title: "Methodology", to: `/docs/datasets/${c.code}/methodology` }]
          : []
      // Coming-soon countries carry the kind-specific label as the meta line.
      // "Documentation coming soon" countries still have datasets, so show the
      // per-location-type items (clicking any lands on the gated coming-soon
      // page). "Data coming soon" has no datasets, so no items.
      if (c.comingSoon) {
        return {
          title: `${c.flag} ${c.name}`.trim(),
          to: `/docs/datasets/${c.code}`,
          meta: comingSoonLabel(c.comingSoon),
          items:
            c.comingSoon === "docs" ? [...items, ...methodologyItems] : [],
        }
      }
      return {
        title: `${c.flag} ${c.name}`.trim(),
        to: `/docs/datasets/${c.code}`,
        meta: buildCountrySummary(data.countries, data.datasets, c.code),
        items: [...items, ...methodologyItems],
      }
    }),
  }
}

function buildConceptsMega(data: DocsCatalogData): MegaPanel {
  // For each location type, collapse variants under a single "metric
  // concept" entry. The grouping key is the canonical display name (cadence
  // / "by X" / parens stripped), so LTMs that share a concept but live
  // under different LTM ids — e.g. `cbg_foot_traffic_month` and
  // `foot_traffic_month` (both "Foot Traffic Month"), or `cbg_trade_area`
  // and `trade_areas_day_al2` (both "Trade Areas …") — collapse into one
  // entry. Single-variant groups link to the concrete LTM page;
  // multi-variant groups still pick the first LTM as the link target.
  const byType = (
    lt: string,
  ): { title: string; to: string }[] => {
    const seenLTMs = new Set<string>()
    const rows: { ltm: string; name: string }[] = []
    for (const d of data.datasets) {
      if (d.locationType !== lt) continue
      if (seenLTMs.has(d.ltm)) continue
      seenLTMs.add(d.ltm)
      rows.push({ ltm: d.ltm, name: d.name })
    }
    const conceptOrder: string[] = []
    const byConcept = new Map<string, typeof rows>()
    for (const r of rows) {
      const concept = metricCanonicalName(r.name)
      if (!byConcept.has(concept)) {
        byConcept.set(concept, [])
        conceptOrder.push(concept)
      }
      byConcept.get(concept)!.push(r)
    }
    conceptOrder.sort((a, b) => a.localeCompare(b))
    return conceptOrder.map((concept) => {
      const members = byConcept.get(concept)!
      // Single-variant concept → skip the overview page and link straight
      // to that LTM. Multi-variant → typed canonical-overview slug.
      const to =
        members.length === 1
          ? `/docs/concepts/metrics/${members[0].ltm}`
          : `/docs/concepts/metrics/${canonicalMetricSlugForType(concept, lt)}`
      return { title: concept, to }
    })
  }

  return {
    columns: [
      {
        icon: MapPinIcon,
        title: "Place",
        to: "/docs/concepts/location-types/place",
        meta: "Activity at specific locations like retail stores or PoIs.",
        items: byType("place"),
      },
      {
        icon: VectorSquareIcon,
        title: "Area",
        to: "/docs/concepts/location-types/area",
        meta: "Activity within and between different areas.",
        items: byType("area"),
      },
      {
        icon: SplitIcon,
        title: "Way",
        to: "/docs/concepts/location-types/way",
        meta: "Traffic flow along roads or public-transport routes.",
        items: byType("way"),
      },
      {
        icon: FlaskConical,
        title: "Techniques",
        to: "/docs/concepts/techniques",
        meta: "How the data is made, general and un-versioned",
        items: liveMethodologyEntries(data.countries).map((m) => ({
          title: m.title,
          to: `/docs/concepts/techniques/${m.id}`,
        })),
      },
    ],
  }
}

function buildApiMega(): MegaPanel {
  // Drills into each Scalar section: Introduction's markdown headings,
  // Datasets operations, and Export operations. Anchors follow Scalar's
  // hash scheme: #description/{slug} for intro headings, and
  // #tag/{tag-slug}/{METHOD}{path} for operations.
  return {
    columns: [
      {
        icon: BookOpen,
        title: "Introduction",
        to: "/docs/api",
        meta: "Auth, pagination, aggregation, errors",
        items: [
          { title: "Guides & examples", to: "/docs/api#description/guides-examples" },
          { title: "Authentication", to: "/docs/api#description/authentication" },
          { title: "Base URL", to: "/docs/api#description/base-url" },
          { title: "Pagination", to: "/docs/api#description/pagination" },
          { title: "Aggregation", to: "/docs/api#description/aggregation" },
          { title: "Errors", to: "/docs/api#description/errors" },
          { title: "Conventions", to: "/docs/api#description/conventions" },
        ],
      },
      {
        icon: Database,
        title: "Datasets",
        to: "/docs/api#tag/datasets",
        meta: "Query records and browse data references",
        items: [
          { title: "List data references", to: "/docs/api#tag/datasets/GET/v3/datasets" },
          { title: "Get data reference", to: "/docs/api#tag/datasets/GET/v3/datasets/{dataReferenceName}" },
          { title: "Run a query", to: "/docs/api#tag/datasets/POST/v3/datasets/{dataReferenceName}:runQuery" },
          { title: "Search field values", to: "/docs/api#tag/datasets/POST/v3/datasets/{dataReferenceName}:searchFieldValues" },
        ],
      },
      {
        icon: Download,
        title: "Export",
        to: "/docs/api#tag/export",
        meta: "Long-running record exports",
        items: [
          { title: "Create records export", to: "/docs/api#tag/export/POST/v3/exports" },
          { title: "List records exports", to: "/docs/api#tag/export/GET/v3/exports" },
          { title: "Get records export", to: "/docs/api#tag/export/GET/v3/exports/{id}" },
          { title: "Delete records export", to: "/docs/api#tag/export/DELETE/v3/exports/{id}" },
        ],
      },
    ],
    footer: [
      { icon: Rocket, label: "Make your first query", to: "/docs/guides/first-query" },
      { icon: BookOpen, label: "Build with AI / MCP", to: "/docs/guides/build-with-ai" },
    ],
  }
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

// The two products a customer logs into — same list as the landing page's
// nav (pine59-landing Header.tsx). Console / API stays a relative link so the
// post-login redirect lands back on the current docs page.
const LOGINS = (pathname: string) => [
  {
    label: "Insights",
    hint: "No code needed",
    href: "https://app.pine59.com",
    event: "insights",
  },
  {
    label: "Console / API",
    hint: "For data teams",
    href: `/login?redirectTo=${encodeURIComponent(pathname)}`,
    event: "console",
  },
]

const SECTIONS: Section[] = [
  { label: "Guides & examples", to: "/docs/guides", mega: buildGuidesMega },
  { label: "Datasets", to: "/docs/datasets", mega: buildCoverageMega },
  { label: "Concepts", to: "/docs/concepts", mega: buildConceptsMega },
  { label: "API", to: "/docs/api", mega: buildApiMega },
]

function isActiveSection(pathname: string, to: string) {
  return pathname === to || pathname.startsWith(`${to}/`)
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function MegaColumnView({ column }: { column: MegaColumn }) {
  const Icon = column.icon
  const dhref = useDocsHref()
  // The heading block — icon + title + meta — laid out so it can live either
  // as plain markup or as a single clickable link. We render it the same way
  // both ways so the hover-as-a-unit treatment is purely background-driven.
  const headingBody = (
    <>
      <div className="mb-1 flex items-center gap-2">
        {Icon && (
          <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-md bg-primary/15 text-primary">
            <Icon className="h-3.5 w-3.5" />
          </span>
        )}
        <span className="text-sm font-semibold text-foreground">
          {column.title}
        </span>
      </div>
      <div className="line-clamp-2 min-h-[2lh] text-xs leading-snug text-muted-foreground">
        {column.meta}
      </div>
    </>
  )
  return (
    <div>
      {column.to ? (
        <NavigationMenuLink asChild>
          {column.to.includes("#") ? (
            <a
              href={dhref(column.to)}
              className="block -mx-2 mb-2 rounded-md px-2 py-1.5 text-foreground hover:bg-muted"
            >
              {headingBody}
            </a>
          ) : (
            <RouterLink
              to={dhref(column.to)}
              className="block -mx-2 mb-2 rounded-md px-2 py-1.5 text-foreground hover:bg-muted"
            >
              {headingBody}
            </RouterLink>
          )}
        </NavigationMenuLink>
      ) : (
        <div className="mb-2.5">{headingBody}</div>
      )}
      {/* Tall columns (e.g. all USA Place LTMs) get an internal scroll so the
          mega panel doesn't push the page chrome around. Each row shows the
          full title via a shadcn Tooltip when the inline label is truncated. */}
      <ul className="flex flex-col gap-0.5 max-h-[60vh] overflow-y-auto -mx-2 px-2">
        {column.items.map((item) => {
          // Hash-bearing links (e.g. /docs/api#tag/datasets/...) need browser-
          // native anchor handling so Scalar's id-based scrolling kicks in.
          // RouterLink would just update the URL hash without triggering it.
          const isHashLink = item.to.includes("#")
          const className =
            "flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[13.5px] text-foreground hover:bg-muted hover:text-foreground"
          const content = (
            <>
              <Tooltip delayDuration={400}>
                <TooltipTrigger asChild>
                  <span className="truncate">{item.title}</span>
                </TooltipTrigger>
                <TooltipContent side="right" align="center">
                  {item.title}
                </TooltipContent>
              </Tooltip>
              {item.trailing && (
                <span className="inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full border bg-muted/40 px-1.5 text-[11px] tabular-nums text-muted-foreground">
                  {item.trailing}
                </span>
              )}
            </>
          )
          return (
            <li key={item.to}>
              <NavigationMenuLink asChild>
                {isHashLink ? (
                  <a href={dhref(item.to)} className={className}>
                    {content}
                  </a>
                ) : (
                  <RouterLink to={dhref(item.to)} className={className}>
                    {content}
                  </RouterLink>
                )}
              </NavigationMenuLink>
            </li>
          )
        })}
        {column.tail && (
          <li className="mt-1">
            <NavigationMenuLink asChild>
              <RouterLink
                to={dhref(column.tail.to)}
                className="block rounded-md px-2 py-1.5 text-[13.5px] font-medium text-primary hover:bg-primary/10"
              >
                {column.tail.title}
              </RouterLink>
            </NavigationMenuLink>
          </li>
        )}
      </ul>
    </div>
  )
}

function MegaPanelView({ panel }: { panel: MegaPanel }) {
  const dhref = useDocsHref()
  const colCount = panel.gridCols ?? panel.columns.length
  // 200px per visual column + 28px gaps + 52px padding, capped at 960px.
  const minWidth = Math.min(960, colCount * 200 + (colCount - 1) * 28 + 52)
  return (
    <div
      className="px-6 py-5"
      style={{ width: `${minWidth}px`, maxWidth: "calc(100vw - 1.5rem)" }}
    >
      <div
        className="grid gap-7"
        style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}
      >
        {panel.columns.map((col) => (
          <MegaColumnView key={col.title} column={col} />
        ))}
      </div>
      {panel.extra}
      {panel.footer && panel.footer.length > 0 && (
        <div className="mt-[18px] flex flex-wrap gap-4 border-t pt-3.5 text-[13px]">
          {panel.footer.map((f) => {
            const Icon = f.icon
            return (
              <NavigationMenuLink asChild key={f.to + f.label}>
                <RouterLink
                  to={dhref(f.to)}
                  className="inline-flex items-center gap-1.5 text-primary hover:underline"
                >
                  <Icon className="h-3.5 w-3.5" />
                  {f.label}
                </RouterLink>
              </NavigationMenuLink>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mobile menu (< md)
// ---------------------------------------------------------------------------

// Docs-internal link that also closes the sheet. Hash links (Scalar anchors)
// stay native <a> so id-based scrolling works, same as in the mega panel.
function MobileMenuLink({
  to,
  onNavigate,
  className,
  children,
}: {
  to: string
  onNavigate: () => void
  className?: string
  children: React.ReactNode
}) {
  const dhref = useDocsHref()
  const href = dhref(to)
  return to.includes("#") ? (
    <a href={href} onClick={onNavigate} className={className}>
      {children}
    </a>
  ) : (
    <RouterLink to={href} onClick={onNavigate} className={className}>
      {children}
    </RouterLink>
  )
}

/**
 * Hamburger menu for narrow viewports. The hover-driven mega menu doesn't
 * work on touch, and four section tabs plus the login controls don't fit in
 * a 52px bar on a phone, so below `md` the top bar collapses to logo +
 * hamburger and this sheet carries the whole navigation: one accordion row
 * per section (the active one starts expanded) with the same columns and
 * items the mega panel shows on desktop, plus the logged-out CTAs.
 */
function DocsMobileMenu({
  authed,
  catalog,
}: {
  authed: boolean
  catalog: DocsCatalogData
}) {
  const { pathname } = useLocation()
  const dhref = useDocsHref()
  const [open, setOpen] = useState(false)
  const close = () => setOpen(false)

  // Belt and braces: links close the sheet on click, but back/forward
  // navigation while it's open should dismiss it too.
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  const activeSection = SECTIONS.find((s) =>
    isActiveSection(pathname, dhref(s.to)),
  )
  const rowClass =
    "flex items-center justify-between gap-2 rounded-md px-2 py-2 text-sm text-foreground active:bg-muted"

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          shape="rectangle"
          aria-label="Open navigation"
        >
          <Menu className="!h-5 !w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-sm"
      >
        <SheetHeader className="border-b px-4 py-3 text-left">
          <SheetTitle className="text-base">Documentation</SheetTitle>
          <SheetDescription className="sr-only">
            Browse the documentation sections
          </SheetDescription>
        </SheetHeader>
        <nav className="min-h-0 flex-1 overflow-y-auto">
          {/* Keyed on the active section so the default-open row re-resolves
              each time the sheet mounts (SheetContent unmounts when closed). */}
          <Accordion
            type="multiple"
            defaultValue={activeSection ? [activeSection.to] : []}
          >
            {SECTIONS.map((s) => {
              const active = activeSection?.to === s.to
              const panel = s.mega?.(catalog)
              return (
                <AccordionItem key={s.to} value={s.to} className="px-4">
                  <AccordionTrigger
                    className={cn(
                      "py-3.5 text-[15px] hover:no-underline",
                      active && "font-semibold text-primary",
                    )}
                  >
                    {s.label}
                  </AccordionTrigger>
                  <AccordionContent className="pb-3">
                    <MobileMenuLink
                      to={s.to}
                      onNavigate={close}
                      className={cn(
                        rowClass,
                        "font-medium text-primary active:bg-primary/10",
                      )}
                    >
                      <span>{s.label} overview</span>
                      <ArrowRight className="h-4 w-4 shrink-0" />
                    </MobileMenuLink>
                    {panel?.columns.map((col) => {
                      const Icon = col.icon
                      const heading = (
                        <span className="flex items-center gap-2">
                          {Icon && (
                            <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-md bg-primary/15 text-primary">
                              <Icon className="h-3.5 w-3.5" />
                            </span>
                          )}
                          <span className="text-sm font-semibold text-foreground">
                            {col.title}
                          </span>
                        </span>
                      )
                      return (
                        <div key={col.title} className="mt-3">
                          {col.to ? (
                            <MobileMenuLink
                              to={col.to}
                              onNavigate={close}
                              className={cn(rowClass, "py-1.5")}
                            >
                              {heading}
                            </MobileMenuLink>
                          ) : (
                            <div className="px-2 py-1.5">{heading}</div>
                          )}
                          {col.items.length > 0 && (
                            <ul className="ml-3 flex flex-col border-l pl-2">
                              {col.items.map((item) => (
                                <li key={item.to}>
                                  <MobileMenuLink
                                    to={item.to}
                                    onNavigate={close}
                                    className={rowClass}
                                  >
                                    <span className="truncate">{item.title}</span>
                                    {item.trailing && (
                                      <span className="inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full border bg-muted/40 px-1.5 text-[11px] tabular-nums text-muted-foreground">
                                        {item.trailing}
                                      </span>
                                    )}
                                  </MobileMenuLink>
                                </li>
                              ))}
                              {col.tail && (
                                <li>
                                  <MobileMenuLink
                                    to={col.tail.to}
                                    onNavigate={close}
                                    className={cn(rowClass, "font-medium text-primary")}
                                  >
                                    {col.tail.title}
                                  </MobileMenuLink>
                                </li>
                              )}
                            </ul>
                          )}
                        </div>
                      )
                    })}
                    {panel?.footer && panel.footer.length > 0 && (
                      <div className="mt-3 flex flex-col border-t pt-2">
                        {panel.footer.map((f) => {
                          const Icon = f.icon
                          return (
                            <MobileMenuLink
                              key={f.to + f.label}
                              to={f.to}
                              onNavigate={close}
                              className={cn(rowClass, "justify-start text-primary")}
                            >
                              <Icon className="h-3.5 w-3.5 shrink-0" />
                              {f.label}
                            </MobileMenuLink>
                          )
                        })}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              )
            })}
          </Accordion>
        </nav>
        {!authed && (
          <div className="border-t p-4">
            <Button asChild shape="rectangle" className="w-full">
              <a href="https://pine59.com/get-started">
                Get access
                <ArrowUpRight />
              </a>
            </Button>
            <div className="mt-4 px-1 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
              Log in
            </div>
            <div className="mt-1 overflow-hidden rounded-[6px] border">
              {LOGINS(pathname).map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() =>
                    captureEvent("login_clicked", { product: l.event })
                  }
                  className="flex items-center justify-between gap-4 border-b px-4 py-3 last:border-b-0 active:bg-accent"
                >
                  <span className="text-sm font-medium text-foreground">
                    {l.label}
                  </span>
                  <span className="whitespace-nowrap font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                    {l.hint}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

export function DocsTopBar() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const dhref = useDocsHref()
  const loginRef = useRef<HTMLDetailsElement>(null)

  // Native <details> only toggle on their summary; close the login menu when
  // the user clicks (or tabs) anywhere outside it, or presses Escape. Same
  // pattern as the landing page's Header.
  useEffect(() => {
    const close = (e: Event) => {
      const el = loginRef.current
      if (el?.open && !el.contains(e.target as Node)) el.open = false
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && loginRef.current) loginRef.current.open = false
    }
    document.addEventListener("pointerdown", close)
    document.addEventListener("focusin", close)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("pointerdown", close)
      document.removeEventListener("focusin", close)
      document.removeEventListener("keydown", onKey)
    }
  }, [])
  const authed =
    (useRouteLoaderData("routes/_console") as { authed?: boolean } | undefined)
      ?.authed ?? false
  // The _console.docs route loader hands us the joined countries + datasets
  // catalog. If the topbar somehow renders outside the docs section the
  // fallback keeps the bar functional (Coverage mega menu just renders empty).
  const docsCatalog =
    (useRouteLoaderData("routes/_console.docs") as DocsCatalogData | undefined) ?? {
      countries: [],
      datasets: [],
    }

  // Tab-style active indicator: a primary-coloured underline plus stronger
  // text weight, so the current section is unambiguous on the white topbar.
  // (bg-accent/50 on its own is too pale against bg-card to read as "selected".)
  const activeClass =
    "relative font-semibold text-primary " +
    "after:absolute after:inset-x-3 after:-bottom-px after:h-[2px] after:rounded-full after:bg-primary"

  return (
    <div className="relative z-50 grid h-[52px] grid-cols-[1fr_auto_1fr] items-center border-b bg-card px-4 md:px-5">
      {/* Logged-out chrome — only for logged-out visitors, who have no
          sidebar. Wordmark left, "Log in" right, both in an overlay using the
          same container math as the docs pages (px-12 + centered
          max-w-screen-xl) so they line up with the content edges like the
          landing page header. Authed users get the logo in the console
          sidebar and no login control here. */}
      <div />
      {!authed && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-full px-4 md:px-12">
          <div className="mx-auto flex h-full w-full max-w-screen-xl items-center justify-between">
            <RouterLink
              to={dhref("/docs")}
              className="pointer-events-auto inline-flex items-center"
            >
              <img
                src="/brand/logo-full.svg"
                alt="Pine59"
                className="h-6 w-auto"
              />
            </RouterLink>
            {/* Log in (secondary) + Get access (primary CTA) — mirrors the
                landing nav's Forest DS pairing. The CTA points at the landing
                site's lead page (the old /request-access path 301s there). */}
            <div className="pointer-events-auto hidden items-center gap-3 md:flex">
              {/* Log in — same <details> disclosure as the landing page nav:
                  a Forest DS secondary button (olive hairline via inset
                  shadow, hay fill on hover) opening a two-product menu. */}
              <details
                ref={loginRef}
                className="group relative [&_summary::-webkit-details-marker]:hidden"
              >
                <summary
                  className={cn(
                    buttonVariants({ variant: "ghost", shape: "rectangle" }),
                    "cursor-pointer list-none text-primary shadow-[inset_0_0_0_1px_#66640F] hover:bg-[#F8FAEC] hover:text-primary active:shadow-[inset_0_0_0_1.5px_#66640F]",
                  )}
                >
                  Log in
                  <ArrowRight className="transition-transform duration-200 group-open:rotate-90" />
                </summary>
                <div className="absolute right-0 z-30 mt-2 w-72 overflow-hidden rounded-[6px] border bg-white shadow-[0px_12px_32px_rgba(0,41,15,0.14)]">
                  {LOGINS(pathname).map((l) => (
                    <a
                      key={l.label}
                      href={l.href}
                      onClick={() =>
                        captureEvent("login_clicked", { product: l.event })
                      }
                      className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-accent"
                    >
                      <span className="text-sm font-medium text-foreground">
                        {l.label}
                      </span>
                      <span className="whitespace-nowrap font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                        {l.hint}
                      </span>
                    </a>
                  ))}
                </div>
              </details>
              <Button asChild shape="rectangle">
                <a href="https://pine59.com/get-started">
                  Get access
                  <ArrowUpRight />
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Desktop nav. Below `md` the mega menu is replaced by the hamburger
          sheet in the right-hand grid cell (DocsMobileMenu). */}
      <NavigationMenu className="hidden md:flex">
        <NavigationMenuList>
          {SECTIONS.map((s) => {
            const to = dhref(s.to)
            const active = isActiveSection(pathname, to)
            if (!s.mega) {
              return (
                <NavigationMenuItem key={s.to}>
                  <NavigationMenuLink
                    asChild
                    active={active}
                    className={cn(navigationMenuTriggerStyle(), active && activeClass)}
                  >
                    <NavLink to={to}>{s.label}</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )
            }
            return (
              <NavigationMenuItem key={s.to}>
                <NavigationMenuTrigger
                  data-active={active ? "" : undefined}
                  className={cn(active && activeClass)}
                  onClick={(e) => {
                    // Mouse/touch clicks navigate to the section page;
                    // hover still opens the mega menu via Radix.
                    // e.detail === 0 means keyboard activation — leave that to
                    // Radix so Enter/Space still expands the menu for a11y.
                    if (e.detail !== 0) navigate(to)
                  }}
                >
                  {s.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <MegaPanelView panel={s.mega(docsCatalog)} />
                </NavigationMenuContent>
              </NavigationMenuItem>
            )
          })}
        </NavigationMenuList>
      </NavigationMenu>

      <div className="col-start-3 justify-self-end md:hidden">
        <DocsMobileMenu authed={authed} catalog={docsCatalog} />
      </div>
    </div>
  )
}
